import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { AlertCircle, LifeBuoy } from 'lucide-react'
import { LifelineType, SessionResult, HTTP_STATUS } from '@/constants'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@/stores'
import { apiClient } from '@/services/api'
import Modal from '@/components/ui/Modal'
import BottomSheet from '@/components/ui/BottomSheet'
import { useIsCompact } from '@/hooks/useDevice'
import ContactSupportForm from '@/components/steps/ContactSupportForm'

interface LifelineProps {
  onClose?: () => void
}

const Lifeline = ({ onClose }: LifelineProps) => {
  const navigate = useNavigate()
  const session = useSessionStore(state => state.session) as any
  const campaign = useSessionStore(state => state.campaign)
  const showContactFormFn = useSessionStore(state => state.showContactForm)
  const mustLogoutSession = useSessionStore(state => state.mustLogoutSession)
  const closeLifeline = useSessionStore(state => state.closeLifeline)
  const resetLifeline = useSessionStore(state => state.resetLifeline)
  const resetIdleTimer = useSessionStore(state => state.resetIdleTimer)
  const logoutSession = useSessionStore(state => state.logout)
  const updateSession = useSessionStore(state => state.updateSession)
  const setLoading = useUIStore(state => state.setLoading)
  const setForceDisplayLoader = useUIStore(state => state.setForceDisplayLoader)
  const addUIMessage = useUIStore(state => state.addUIMessage)
  const clearUIMessages = useUIStore(state => state.clearUIMessages)
  const clearError = useUIStore(state => state.clearError)
  const isLoading = useUIStore(state => state.isLoading)
  const uiError = useUIStore(state => state.error)
  const isCompact = useIsCompact()

  const [lifelineType, setLifelineType] = useState<number | null>(null)
  const [contacted, setContacted] = useState(false)
  const [closing, setClosing] = useState(false)
  const [notifying, setNotifying] = useState(false)
  const notifyingRef = useRef(false)
  const initializedRef = useRef(false)
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [phoneFallbackReason, setPhoneFallbackReason] = useState<string | null>(null)
  // Mirror ContactSupportForm's RHF validity so the bottom-sheet footer's
  // submit button can show the correct disabled state.
  const [formIsValid, setFormIsValid] = useState(false)
  const [formIsSubmitting, setFormIsSubmitting] = useState(false)
  const LIFELINE_FORM_ID = 'lifeline-contact-form'

  // Contact-form surface only; lifeline steps render in SessionContainer's modal.
  const wantsContactForm = !!showContactFormFn?.()
  const isOpen = !closing && wantsContactForm

  useEffect(() => {
    // Re-arm on the store flag, never on isOpen: resetting `closing` while the flag is
    // still set reopened the sheet the instant the user dismissed it.
    if (!wantsContactForm) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Open/close lifecycle: re-arming during render reopened the sheet on dismiss.
      setContacted(false)
      setClosing(false)
      setPhoneFallbackReason(null)
      initializedRef.current = false
      return
    }

    if (!isOpen) {
      return
    }

    if (initializedRef.current) {
      return
    }
    initializedRef.current = true

    // Release loader flags on open so the sheet shows content and stays closeable.
    setForceDisplayLoader(false)
    setLoading(false)

    let lt: number = LifelineType.Email
    if (session?.lifeline_type) {
      lt = session.lifeline_type
    }

    if (lt === LifelineType.Email && !(campaign as any)?.support_email_fields?.length) {
      lt = LifelineType.Phone
    }
    if (lt === LifelineType.Phone && !session?.phone_contact_url) {
      lt = LifelineType.Email
    }

    setLifelineType(lt)
    setFormErrors({})
    setFormValues({})
  }, [wantsContactForm, isOpen, session, campaign, setForceDisplayLoader, setLoading])

  const close = () => {
    setLifelineType(null)
    setClosing(true)
    // Whatever branch follows, the error that opened this must not survive the
    // dismiss — it feeds showContactForm() and would reopen the sheet.
    clearError()

    // can_exit_lifeline decides whether dismissing ends the session.
    if (session?.can_exit_lifeline) {
      clearUIMessages()
      resetLifeline()
      resetIdleTimer()
      closeLifeline().catch(() => {})
      onClose?.()
      return
    }

    if (mustLogoutSession?.()) {
      logoutSession()
      const msg = 'Session ended.'
      navigate(`/logout?msg=${encodeURIComponent(msg)}`, { state: { msg }, replace: true })
      return
    }

    complete()
  }

  const complete = (msg: string | null = null) => {
    setClosing(true)
    setLoading(true)

    const finish = () => {
      onClose?.()
      if (msg) {
        addUIMessage({ type: 'success', message: msg })
      }
      resetLifeline()
      const qs = msg ? `?msg=${encodeURIComponent(msg)}` : ''
      navigate(`/logout${qs}`, { state: { msg }, replace: true })
      setLoading(false)
    }

    if (notifyingRef.current) {
      const wait = () => {
        if (notifyingRef.current) {
          setTimeout(wait, 300)
        } else {
          finish()
        }
      }
      setTimeout(wait, 300)
    } else {
      finish()
    }
  }

  const setNotifyingBoth = (v: boolean) => {
    notifyingRef.current = v
    setNotifying(v)
  }

  const sendMessage = async (contactForm: Record<string, any>) => {
    setNotifyingBoth(false)
    setLoading(true)

    const sessionId = session?.id
    if (!sessionId) {
      // No active session to attach the message to — sending would 404. This
      // shouldn't be reachable (the form only renders with a session), but guard
      // so a stale open state can't fire a request against a missing session.
      setLoading(false)
      return
    }

    try {
      await apiClient.post(`/sessions/${sessionId}/email/`, contactForm)
      setContacted(true)
      setLoading(false)
    } catch (err: any) {
      console.error(err)

      if (
        session?.lifeline_status === SessionResult.LifelineNoMatch &&
        err.response?.status === HTTP_STATUS.BAD_REQUEST
      ) {
        setLoading(false)
        setFormValues(contactForm)
        setFormErrors(err.response.data || {})
      } else {
        setLifelineType(LifelineType.Phone)
        setPhoneFallbackReason("We couldn't send your message. Please give us a call instead.")
        setNotifyingBoth(true)
        try {
          await updateSession({
            result: SessionResult.LifelineContactFailed,
            message: err?.error?.message || err?.message || null,
          })
        } catch {
          /* best-effort */
        } finally {
          setNotifyingBoth(false)
          setLoading(false)
        }
      }
    }
  }

  // No channel to render (no email fields / phone url) → show a closeable fallback.
  const emailFields = ((campaign as any)?.support_email_fields || []).filter(Boolean)
  const hasPhone = !!session?.phone_contact_url
  const canRenderContact =
    contacted ||
    (lifelineType === LifelineType.Phone && hasPhone) ||
    (lifelineType === LifelineType.Email && emailFields.length > 0)

  const body = isLoading ? (
    <div className="lifeline-loading">
      <div className="lifeline-spinner" />
      {notifying && (
        <p className="text-fluid-base" style={{ color: 'var(--light-text-color)' }}>
          Notifying support...
        </p>
      )}
    </div>
  ) : (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] as const }}
      className="lifeline-card">
      {uiError && (
        <div className="lifeline-error">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <span>{(uiError as any)?.message || String(uiError)}</span>
        </div>
      )}

      {session && canRenderContact ? (
        <ContactSupportForm
          lifelineType={lifelineType}
          contacted={contacted}
          formErrors={formErrors}
          formValues={formValues}
          phoneFallbackReason={phoneFallbackReason}
          onEmail={sendMessage}
          hideSubmit={isCompact && !isLoading && !contacted}
          onFormStateChange={({ isValid, isSubmitting }) => {
            setFormIsValid(isValid)
            setFormIsSubmitting(isSubmitting)
          }}
          formId={LIFELINE_FORM_ID}
        />
      ) : (
        <div className="lifeline-fallback">
          <span className="lifeline-fallback-icon">
            <LifeBuoy size={30} strokeWidth={1.6} />
          </span>
          <h3 className="lifeline-fallback-title">We're here to help</h3>
          <p className="lifeline-fallback-text">
            No support channel is available right now. Please try again in a moment.
          </p>
          <div className="lifeline-fallback-actions">
            {hasPhone && (
              <a href={session?.phone_contact_url} className="btn-base lifeline-channel-cta">
                Call us
              </a>
            )}
            <button type="button" onClick={close} className="btn-base back-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </motion.div>
  )

  // Only show the sheet-footer submit when the contact form is actually
  // rendered (session present + a channel to render). Otherwise the button sits
  // over the fallback body permanently disabled — its validity never syncs
  // because ContactSupportForm (which reports it) never mounts.
  const showFooterCta =
    isCompact && !isLoading && !contacted && lifelineType === LifelineType.Email && !!session && canRenderContact
  const footer = showFooterCta ? (
    <button
      type="submit"
      form={LIFELINE_FORM_ID}
      className="btn-base w-full"
      disabled={!formIsValid || formIsSubmitting}
      style={{
        backgroundColor: 'var(--theme-color)',
        color: 'var(--button-text-color, #fff)',
        borderColor: 'var(--theme-color)',
      }}>
      Send Message
    </button>
  ) : undefined

  if (isCompact) {
    return (
      <BottomSheet isOpen={!!isOpen} onClose={close} title="Need a hand?" footer={footer}>
        <div className="px-5 pb-4">{body}</div>
      </BottomSheet>
    )
  }

  return (
    <Modal isOpen={!!isOpen} onClose={close} size="lg">
      {body}
    </Modal>
  )
}

export default Lifeline
